import type { ColDef, ColumnState, GridApi } from 'ag-grid-community'
import { Settings } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { Button } from '@/components/lemmala-design-system/button/button'
import { DropdownSelect } from '@/components/lemmala-design-system/dropdown/dropdown-select'
import type { DropdownSelectItem } from '@/components/lemmala-design-system/dropdown/dropdown-select.types'
import { useAgGridContext } from '@/components/lemmala-design-system/table'
import { TABLE_COLUMN_CUSTOMIZATION_LABELS } from '@/components/lemmala-design-system/table/labels'
import type { ColumnItem } from '@/components/lemmala-design-system/table/types'

export interface AgColumnCustomizationProps<TRowData = Record<string, unknown>> {
  gridApi: GridApi<TRowData> | null
  defaultColDefs: ColDef<TRowData>[]
}

export const AgColumnCustomization = <TRowData = Record<string, unknown>,>({
  gridApi,
  defaultColDefs,
}: AgColumnCustomizationProps<TRowData>) => {
  const ctx = useAgGridContext<TRowData>()
  const [isOpen, setIsOpen] = useState(false)
  const [columns, setColumns] = useState<ColumnItem[]>([])

  useEffect(() => {
    if (!gridApi || !isOpen) return

    const columnDefs = gridApi.getColumns()
    if (!columnDefs) return

    const columnState = gridApi.getColumnState()

    const columnData = columnDefs
      .filter((col) => {
        const colDef = col.getColDef()
        // Exclude columns that should not appear in the tool panel
        return !colDef.suppressColumnsToolPanel
      })
      .map((col) => {
        const colId = col.getColId()
        const state = Array.isArray(columnState)
          ? columnState.find((s) => s.colId === colId)
          : undefined
        const isHidden = state?.hide ?? col.getColDef().hide ?? false

        return {
          id: colId,
          label: col.getColDef().headerName || colId,
          checked: !isHidden,
        }
      })

    // oxlint-disable-next-line react/set-state-in-effect
    setColumns(columnData)
  }, [gridApi, isOpen])

  const handleItemsChange = useCallback(
    (newColumns: ColumnItem[]) => {
      if (!gridApi) return

      setColumns(newColumns)

      // Create column state that preserves the current order from our columns array
      const columnState = newColumns.map((col) => {
        const currentState = gridApi.getColumnState().find((s) => s.colId === col.id)
        return {
          colId: col.id,
          hide: !col.checked,
          ...(currentState && {
            pinned: currentState.pinned,
            width: currentState.width,
            flex: currentState.flex,
            sort: currentState.sort,
            sortIndex: currentState.sortIndex,
            aggFunc: currentState.aggFunc,
            rowGroup: currentState.rowGroup,
            rowGroupIndex: currentState.rowGroupIndex,
            pivot: currentState.pivot,
            pivotIndex: currentState.pivotIndex,
          }),
        }
      })

      // Apply state with explicit ordering
      gridApi.applyColumnState({ state: columnState, applyOrder: true })
    },
    [gridApi],
  )

  // Handle reset to default
  const handleReset = useCallback(() => {
    if (!gridApi || !ctx?.onColumnStateReset) return

    ctx.onColumnStateReset()

    const defaultColumnState = defaultColDefs
      .map((def) => {
        const colId = (def.colId as string) ?? (def.field as string) ?? ''
        if (!colId) return null
        const pinnedVal =
          typeof def.pinned === 'string' ? def.pinned : def.pinned ? 'left' : undefined
        return {
          colId,
          hide: def.hide ?? false,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: def.flex,
          width: def.width,
          ...(pinnedVal ? { pinned: pinnedVal } : {}),
        } as ColumnState
      })
      .filter(Boolean) as ColumnState[]

    gridApi.applyColumnState({ state: defaultColumnState, applyOrder: true })

    const columnData = defaultColDefs
      .filter((def) => !def.suppressColumnsToolPanel) // Exclude columns that shouldn't appear in tool panel
      .map((def) => {
        const colId = (def.colId as string) ?? (def.field as string) ?? ''
        if (!colId) return null
        return {
          id: colId,
          label: def.headerName || colId,
          checked: !(def.hide ?? false),
        }
      })
      .filter(Boolean) as ColumnItem[]

    setColumns(columnData)
  }, [gridApi, defaultColDefs, ctx])

  return (
    <DropdownSelect
      open={isOpen}
      onOpenChange={setIsOpen}
      className='!w-56'
      trigger={
        <Button
          type='button'
          variant='outline'
          size='icon'
          aria-label={TABLE_COLUMN_CUSTOMIZATION_LABELS.editColumns}
          className='shrink-0'
        >
          <Settings className='size-4 text-main-foreground-secondary' />
        </Button>
      }
      popoverAlign='end'
      search
      footerContent={
        <Button
          variant='link'
          onClick={(e) => {
            e.preventDefault()
            handleReset()
          }}
        >
          {TABLE_COLUMN_CUSTOMIZATION_LABELS.resetToDefault}
        </Button>
      }
      items={columns.map<DropdownSelectItem>(({ id, label }) => ({ id, label }))}
      multi
      draggable
      selectedItems={columns.filter((c) => c.checked).map((c) => c.id)}
      onMultiValueChange={(selectedIds) => {
        const updated = columns.map((c) => ({ ...c, checked: selectedIds.includes(c.id) }))
        handleItemsChange(updated)
      }}
      onReorder={(newOrder) => {
        const idToColumn = new Map(columns.map((c) => [c.id, c]))
        const reordered = newOrder
          .map((item) => idToColumn.get(item.id))
          .filter(Boolean) as ColumnItem[]
        handleItemsChange(reordered)
      }}
      searchPlaceholder={TABLE_COLUMN_CUSTOMIZATION_LABELS.searchColumns}
    />
  )
}

AgColumnCustomization.displayName = 'AgColumnCustomization'
